import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const locales = ['en', 'so'];

interface SEOProps {
  title: string;
  description: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: string;
  datePublished?: string;
  dateModified?: string;
  jsonLd?: object[];
  /**
   * Keep the page out of the search index. Only for routes that must never
   * appear on Google: the 404 view, internal demos and private/utility pages.
   * Everything public leaves this false so it stays indexable.
   */
  noIndex?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = [],
  image = '/assets/images/logo white blue light.webp',
  url,
  type = 'website',
  datePublished,
  dateModified,
  jsonLd = [],
  noIndex = false
}) => {
  const location = useLocation();
  const origin = process.env.REACT_APP_BASE_URL || 'https://mybank.so';
  const path = location.pathname;
  
  // Canonical path. Three normalisations, in order:
  //   1. drop the vestigial /so locale prefix - Somali is a client-side language
  //      switch, not a separate URL space, and the server 301s /so/* away. A
  //      canonical pointing at /so/x would name a URL that redirects.
  //   2. drop the trailing slash - the server 301s /x/ to /x.
  //   3. query strings never reach the canonical.
  const localeStripped = path.replace(/^\/so(?=\/|$)/, '') || '/';
  const cleanPath = localeStripped.replace(/(.)\/$/, '$1') || '/';
  const currentUrl = url || `${origin}${cleanPath}`;

  // Determine primary locale for OG tags
  const ogLocale = path.startsWith('/so') ? 'so_SO' : 'en_US';
  const ogAltLocale = ogLocale === 'en_US' ? 'so_SO' : 'en_US';

  // Base WebPage schema
  const webPageSchema: any = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": currentUrl,
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    "publisher": {
      "@type": "Organization",
      "name": "MyBank LTD Somalia",
      "logo": {
        "@type": "ImageObject",
        "url": `${origin}/assets/images/logo white blue light.webp`
      }
    }
  };

  // Homepage‑only LocalBusiness schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "BankOrCreditUnion",
    "name": "MyBank LTD Somalia",
    "image": "https://mybank.so/assets/images/logo white blue light.webp",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Mogadishu Main Branch",
      "addressLocality": "Mogadishu",
      "addressRegion": "Benadir",
      "addressCountry": "Somalia"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 2.0260528,
      "longitude": 45.3103786
    },
    "url": "https://mybank.so",
    "telephone": "+252-613737777",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday","Sunday","Monday","Tuesday","Wednesday","Thursday"],
        "opens": "08:00",
        "closes": "16:30"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "08:00",
        "closes": "16:30"
      }
    ],
    "areaServed": "SO",
    "sameAs": [
      "https://www.facebook.com/MyBankLTD/",
      "https://x.com/mybankltd?s=21",
      "https://www.linkedin.com/company/mybankltd/",
      "https://www.instagram.com/mybankltd?igsh=b2hsYjVlOGhxYWgz"
    ]
  };

  // Homepage‑only WebSite + SearchAction schema
  const webSiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "url": "https://mybank.so/",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://mybank.so/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  // Compose all JSON‑LD
  // Organization schema to reinforce brand identity and correct knowledge panel associations
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://mybank.so/#organization",
    "name": "MyBank LTD Somalia",
    "legalName": "MyBank Limited",
    "alternateName": ["MyBank Somalia", "MyBank LTD", "MyBank"],
    "url": "https://mybank.so/",
    "logo": {
      "@type": "ImageObject",
      "url": `${origin}/android-chrome-512x512.png`,
      "width": 512,
      "height": 512
    },
    "sameAs": [
      "https://www.facebook.com/MyBankLTD/",
      "https://x.com/mybankltd?s=21",
      "https://www.linkedin.com/company/mybankltd/",
      "https://www.instagram.com/mybankltd?igsh=b2hsYjVlOGhxYWgz"
    ],
    "contactPoint": [{
      "@type": "ContactPoint",
      "telephone": "+252-613737777",
      "contactType": "customer service",
      "areaServed": "SO",
      "availableLanguage": ["en", "so"]
    }]
  };

  const allSchemas = [
    webPageSchema,
    ...(path === '/' || path === '/so/' || path === '/so' ? [organizationSchema, localBusinessSchema, webSiteSchema] : []),
    ...jsonLd
  ];

  // Resource hints (avoid aggressive preloads that trigger warnings)
  React.useEffect(() => {
    // Only hint resources for the homepage hero; other routes load on demand
    if (path === '/') {
      const prefetchImage = (src: string) => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.as = 'image';
        link.href = src;
        document.head.appendChild(link);
      };

      prefetchImage('/assets/images/black Mastercard.webp');
      prefetchImage('/assets/images/blue-light Mastercard.webp');
      prefetchImage('/assets/images/mobile mockup1.webp');
    }

    // DNS prefetch for external resources
    const dnsPrefetch = (href: string) => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = href;
      document.head.appendChild(link);
    };

    dnsPrefetch('https://fonts.googleapis.com');
    dnsPrefetch('https://fonts.gstatic.com');
    dnsPrefetch('https://cdn.jsdelivr.net');
  }, [path]);

  return (
    <Helmet>
      {/* Title & Meta */}
      <title>{`${title} | MyBank LTD Somalia`}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}

      {/* Canonical (single-language indexable URLs; Somali is client-side only).
          Omitted on noindex pages: a canonical is a request to index THIS url,
          which contradicts the robots directive right below it. */}
      {!noIndex && <link rel="canonical" href={currentUrl} />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${origin}${image}`} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="MyBank LTD Somalia" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogAltLocale} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@mybankltd" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${origin}${image}`} />

      {/* Robots. Exactly one robots directive per page - a second tag injected
          from anywhere else would contradict this one and Google resolves the
          conflict by taking the most restrictive, which is how public pages
          silently fall out of the index. */}
      <meta
        name="robots"
        content={
          noIndex
            ? 'noindex, nofollow'
            : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
        }
      />
      <meta name="author" content="MyBank LTD Somalia" />
      <meta name="publisher" content="MyBank LTD Somalia" />
      <meta name="copyright" content="MyBank LTD Somalia" />
      <meta name="geo.region" content="SO" />
      <meta name="geo.placename" content="Mogadishu, Somalia" />
      <meta name="geo.position" content="2.046934;45.318161" />
      <meta name="ICBM" content="2.046934, 45.318161" />
      <meta name="language" content="English" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="revisit-after" content="1 days" />
      {/* JSON‑LD Scripts */}
      {allSchemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {/* Font optimization */}
      <link 
        rel="stylesheet" 
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Poppins:wght@300;400;500;600;700&display=swap"
        media="print"
        onLoad={(e) => {
          const target = e.target as HTMLLinkElement;
          target.media = 'all';
        }}
      />
      <noscript>{`
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Poppins:wght@300;400;500;600;700&display=swap">
      `}</noscript>
      
    </Helmet>
  );
};

export default SEO;
